import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Avatar,
    Badge,
    Box,
    Chip,
    Button,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemIcon,
    ListItemSecondaryAction,
    ListItemText,
    Paper,
    styled,
    TextField,
    Typography,
    useTheme
} from "@mui/material";
import dayjs from "dayjs";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import * as React from "react";
import {useCallback, useState} from "react";
import {useApplicationContext} from "../ApplicationContext";
import {DownloadFileButton} from "./DownloadFileButton";
import {MarkdownRenderer} from "./MarkdownRenderer";
import {DeleteFileDialg} from "./Admin/DeleteFileDialog";
import {SxProps} from "@mui/system";
import {Theme} from "@mui/material/styles";
import {
    EUserGroup,
    EVersionStage,
    GetVersionData,
    GetVersionDataCustomer,
    VersionVersionFileData
} from "../apiClient/Api";

import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {
    faPlus,
    faStars,
    faFileArrowDown,
    faUser,
    faTriangleExclamation,
    faDownload,
    faWandMagicSparkles,
    faCalendar,
    faTrash,
    faCaretDown
} from '@fortawesome/pro-solid-svg-icons'
import {useTranslation} from "react-i18next";import { getStageLabel } from "../utils/versionUtils";
export interface VersionItemProps {
    data: GetVersionData | GetVersionDataCustomer
    reloadCallback?: () => void
}

type HostSystemFileGroup = {
    id: string
    name: string
    files: VersionVersionFileData[]
}


const Item = styled(Paper)(({theme}) => ({
    backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
    ...theme.typography.body2,

    textAlign: 'left',
    color: theme.palette.text.secondary,
}));

/**
 * Groups version files by host system in first-seen order.
 */
const groupFilesByHostSystem = (
    files: VersionVersionFileData[] | undefined,
    unknownLabel: string
): HostSystemFileGroup[] => {
    const groups = new Map<string, HostSystemFileGroup>();
    const order: string[] = [];
    (files ?? []).forEach((file) => {
        const groupId = file.hostSystemId ?? 'unknown';
        const preferredName = file.hostSystemName?.trim() ?? '';
        const groupName = preferredName || unknownLabel;
        if (!groups.has(groupId)) {
            groups.set(groupId, {id: groupId, name: groupName, files: []});
            order.push(groupId);
        }
        const group = groups.get(groupId);
        if (group) {
            if (preferredName && group.name === unknownLabel) {
                group.name = preferredName;
            }
            group.files.push(file);
        }
    });
    return order.map((id) => groups.get(id)).filter(Boolean) as HostSystemFileGroup[];
};


export const VersionItem = (props: VersionItemProps) => {
    const {t} = useTranslation();
    const changes = props.data.changes;
    const context = useApplicationContext();
    const theme = useTheme();
    const [downloadsOpen, setDownloadsOpen] = useState(false)
    const [sendMailToAllOpen, setSendMailToAllOpen] = useState(false)

    const [selectedFile, setSelectedFile] = useState<VersionVersionFileData | undefined>(undefined);
    const [selectedFileDelete, setSelectedFileDelete] = useState<string | undefined>(undefined);
    const [mailContent, setMailContent] = useState<string>("")
    const hostSystemUnknownLabel = t('version.item.hostSystemUnknown');
    const groupedFiles = React.useMemo(
        () => groupFilesByHostSystem(props.data.files, hostSystemUnknownLabel),
        [props.data.files, hostSystemUnknownLabel]
    );
    const fileDeleted = useCallback((deleted: boolean) => {
        setSelectedFileDelete(undefined);


        if (deleted) {
            if (props.reloadCallback)
                props.reloadCallback();
        }
    }, [setSelectedFileDelete])

    const FontAwesomeListIcon = styled(ListItemIcon, {})({
        fontSize: 18,
        minWidth: 33,
        color: theme.palette.primary.light
    });

    const FontAwesomeListText = styled(ListItemText, {})({
        fontSize: 14
    });

    const isCritical = props.data.criticalUpdate != undefined && props.data.criticalUpdate.length > 0;
    const handleClose = () => {
        setDownloadsOpen(false);
    }

    const handleCloseMail = () => {
        
        setMailContent("");
        setSendMailToAllOpen(false);
    }

    const sendMailToAll = () => {
       // alert(JSON.stringify(selectedFile))
        //if (selectedFile?.id) {
            if (mailContent == undefined || mailContent.length < 10) {
                alert("Sie müssen mindestens 10 Zeichen eingeben um eine Mail zu verschicken")
                return;
            }
            if (window.confirm("Möchten Sie wirklich eine Mail an alle schicken?")) {
                context.api.version.versionSendMailToUsers(props.data.major!, props.data.minor!, props.data.patch!, mailContent).then(() => handleClose()).catch(context.apiError);
            }
        //}

    }


    const strAny = (str: string | string[] | null | undefined) => {

        if (Array.isArray(str)) {
            return str.filter(e => e.length > 0).length > 0
        }
        return str;
    }

    const listItemStyles: SxProps<Theme> = {
        paddingTop: "2pt", paddingBottom: 0
    }


    const isPrimaryVersion = () => {
        const patch = props.data.patch ?? -1;
        const minor = props.data.minor ?? -1;
        return minor === -1 || patch === -1;

    }

    return (
        <>
            <Item sx={{scrollMargin: 80}}
                  style={{backgroundColor: isCritical ? theme.palette.mode === 'dark' ? "#8F2027" : "#FDD" : undefined}}
                  id={props.data.versionId!}>
                <Box p={{xs: theme.spacing(1), sm: theme.spacing(2)}} pl={{xs: theme.spacing(1), sm: theme.spacing(4)}}>

                    <Typography component="h2" variant="h4" color="primary" textAlign={{xs: "center", sm: "left"}}
                                sx={{paddingBottom: 0, marginBottom: 0}}
                                gutterBottom fontSize={{
                        xs: 25,
                        sm: 40
                    }}>{t('version.item.version', {version: isPrimaryVersion() && "ersion "})}{props.data.major}{(props.data.minor ?? -1) !== -1 && <>.{props.data.minor}{(props.data.patch ?? -1) !== -1 && <>.{props.data.patch}</>}</>}{getStageLabel(props.data.stage, t) ? ` ${getStageLabel(props.data.stage, t)}` : ""}</Typography>
                    <Typography variant={"caption"}><FontAwesomeIcon icon={faCalendar}
                                                                     fontSize={"inherit"}/> {dayjs(props.data.approved).format("DD.MM.YYYY")}
                    </Typography>
                    {isCritical && <Typography component={"p"} variant={"h6"}
                                               color={"error"}>{t('version.item.criticalUpdate', {update: props.data.criticalUpdate})}</Typography>}
                    <Typography variant={"subtitle1"} fontWeight={"bold"}>{props.data.subtitle}</Typography>
                    {strAny(props.data.header) && props.data.minor !== -1 && props.data.patch !== -1 && <>
                        <MarkdownRenderer>{props.data.header!}</MarkdownRenderer>
                    </>}

                    {strAny(changes?.highlights) && <>
                        <Typography variant={"h6"} color={"secondary"}
                                    fontWeight={"bold"}>{t('version.item.highlights')}</Typography>
                        <List>
                            {changes?.highlights?.map(elem => <>
                                <ListItem sx={listItemStyles}>
                                    {elem.length > 0 && <>
                                        <FontAwesomeListIcon><FontAwesomeIcon icon={faStars}/></FontAwesomeListIcon>
                                        <FontAwesomeListText>{elem}</FontAwesomeListText>
                                    </>}
                                </ListItem>
                            </>)}
                        </List>
                    </>
                    }
                    {strAny(changes?.features) && <>
                        <Typography variant={"h6"} color={"secondary"}
                                    fontWeight={"bold"}>{t('version.item.features')}</Typography>
                        <List>
                            {changes?.features?.map(elem => <>
                                <ListItem sx={listItemStyles}>
                                    {elem.length > 0 && <>
                                        <FontAwesomeListIcon><FontAwesomeIcon icon={faPlus}/></FontAwesomeListIcon>
                                        <FontAwesomeListText>{elem}</FontAwesomeListText>
                                    </>}
                                </ListItem>
                            </>)}
                        </List>
                    </>
                    }
                    {strAny(changes?.bugfixes) && <>
                        <Typography variant={"h6"} color={"secondary"}
                                    fontWeight={"bold"}>{t('version.item.bugfixes')}</Typography>
                        <List>
                            {changes?.bugfixes?.map(elem => <>
                                <ListItem sx={listItemStyles}>
                                    {elem.length > 0 && <>
                                        <FontAwesomeListIcon><FontAwesomeIcon
                                            icon={faWandMagicSparkles}/></FontAwesomeListIcon>
                                        <FontAwesomeListText>{elem}</FontAwesomeListText>
                                    </>}
                                </ListItem>
                            </>)}
                        </List>
                    </>
                    }
                    {strAny(props.data.description) && <>
                        <Divider/>
                        <Typography variant={"h5"} color={"secondary"}
                                    fontWeight={"bold"}>{t('version.item.information')}</Typography>
                        <MarkdownRenderer>{props.data.description!}</MarkdownRenderer>
                    </>}
                    {context.isInternal && (props.data as GetVersionData).internalInformation != undefined && strAny((props.data as GetVersionData).internalInformation) && <>
                        <Divider/>
                        <Typography variant={"h5"} color={"secondary"}
                                    fontWeight={"bold"}>{t('version.item.internalInformation')}</Typography>
                        <MarkdownRenderer>{(props.data as GetVersionData).internalInformation!}</MarkdownRenderer>
                    </>}
                    {(props.data.files?.length ?? 0) > 0 && <>
                        <Divider/>
                        <Accordion 
                            defaultExpanded
                            sx={{
                                border: 0,
                                boxShadow: 0,
                                paddingLeft: 0,
                                marginLeft: 0,
                                background: "none",
                                mt: 2
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<FontAwesomeIcon icon={faCaretDown}/>}
                                sx={{
                                    border: 0,
                                    boxShadow: 0,
                                    paddingLeft: 0,
                                    marginLeft: 0,
                                    minHeight: 48
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography
                                        variant="h5" 
                                        color="secondary"
                                        fontWeight="bold"
                                    >
                                        {t('version.item.files')}
                                    </Typography>
                                    <Chip 
                                        label={props.data!.files!.length} 
                                        color="primary"
                                        size="medium"
                                    />
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {groupedFiles.map((group) => (
                                    <Accordion
                                        key={group.id}
                                        defaultExpanded
                                        sx={{
                                            border: 0,
                                            boxShadow: 0,
                                            background: "none",
                                            '&:before': {display: 'none'},
                                            mb: 1
                                        }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<FontAwesomeIcon icon={faCaretDown}/>}
                                            sx={{
                                                paddingLeft: 2,
                                                marginLeft: 0,
                                                minHeight: 40,
                                                '& .MuiAccordionSummary-content': {margin: '8px 0'}
                                            }}
                                        >
                                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                                                <FontAwesomeIcon icon={faFileArrowDown} />
                                                <Typography variant="subtitle1" color="text.primary" fontWeight="600">
                                                    {group.name}
                                                </Typography>
                                                <Chip 
                                                    size="small" 
                                                    color="primary" 
                                                    label={`${group.files.length} ${group.files.length === 1 ? t('version.item.file') : t('version.item.files')}`}
                                                />
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{paddingLeft: 0, paddingRight: 0}}>
                                            <List>
                                                {group.files.map((file) => (
                                                    <ListItem
                                                        key={file.id ?? file.fileName}
                                                        sx={{paddingRight: {xs: '50px', sm: '"95px"'}}}>
                                                        <DownloadFileButton id={file.id!} fileName={file.fileName!}>
                                                            <ListItemAvatar sx={{display: {xs: 'none', sm: 'block'}}}>
                                                                <Avatar>
                                                                    <FontAwesomeIcon icon={faDownload}/>
                                                                </Avatar>
                                                            </ListItemAvatar>
                                                        </DownloadFileButton>
                                                        <DownloadFileButton id={file.id!} fileName={file.fileName!}>
                                                            <ListItemText
                                                                primary={file.type?.name ?? ''}
                                                                secondary={file.fileName}
                                                            />
                                                        </DownloadFileButton>

                                                        <ListItemSecondaryAction sx={{width: {xs: '40px', sm: "90px"}}}>
                                                            {(context.userData?.userGroup ?? 4) <= EUserGroup.OFFICE && (file.downloadedBy?.length ?? 0) > 0 && (
                                                                <IconButton 
                                                                    onClick={() => {
                                                                        setDownloadsOpen(true)
                                                                        setSelectedFile(file)
                                                                    }}
                                                                    size="small"
                                                                >
                                                                    <Badge 
                                                                        badgeContent={file.downloadedBy?.length}
                                                                        color="primary"
                                                                    >
                                                                        <FontAwesomeIcon icon={faUser}/>
                                                                    </Badge>
                                                                </IconButton>
                                                            )}
                                                            {(context.userData?.userGroup ?? 4) <= EUserGroup.ADMIN &&
                                                                <IconButton 
                                                                    color="warning" 
                                                                    sx={{display: {xs: 'none', sm: 'inline'}}}
                                                                    size="small"
                                                                    onClick={() => {
                                                                        setSelectedFileDelete(file.id);
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon icon={faTrash}/>
                                                                </IconButton>
                                                            }
                                                        </ListItemSecondaryAction>

                                                    </ListItem>
                                                ))}
                                            </List>
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </AccordionDetails>
                        </Accordion>


                    </>}
                    {strAny(props.data.footer) && <>
                        <Divider/>
                        <MarkdownRenderer>{props.data.footer!}</MarkdownRenderer>
                    </>}
                    {(context.userData?.userGroup ?? EUserGroup.CUSTOMER) <= EUserGroup.ADMIN &&
                        <Button fullWidth variant={"outlined"} size={"large"}
                                onClick={() => setSendMailToAllOpen(true)}>{t('version.item.sendEmailToAll')}</Button>
                    }
                </Box>
            </Item>
            <DeleteFileDialg fileDeleted={fileDeleted} id={selectedFileDelete}/>


            <Dialog
                open={sendMailToAllOpen}
                fullWidth
                maxWidth={'md'}
                onClose={handleCloseMail}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {t('version.item.sendMail')}
                </DialogTitle>
                <DialogContent>
                    <Typography variant={"caption"}
                                fontWeight={"bold"}>{t('version.item.sendMailToAllDescription')}</Typography>
                    <TextField multiline rows={5} fullWidth value={mailContent}
                               onChange={(e) => setMailContent(e.target.value)}/>
                </DialogContent>
                <DialogActions>
                    <Button color={"warning"} onClick={sendMailToAll}><FontAwesomeIcon
                        icon={faTriangleExclamation}/> {t('version.item.sendEmailToAll')}</Button>
                    <Button onClick={handleCloseMail}>{t('version.item.close')}</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={downloadsOpen}
                fullWidth
                maxWidth={'md'}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {t('version.item.downloads')}
                </DialogTitle>
                <DialogContent>
                    <List>
                        {selectedFile?.downloadedBy?.map(data => <>
                            <ListItem>
                                <ListItemText primary={data.name} secondary={data.eMail}/>
                            </ListItem>
                        </>)}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>{t('version.item.close')}</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
